import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle2,
  AlertCircle,
  CircleDot,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Clock,
  Link2,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Location } from "@shared/schema";

interface NearbyArea {
  name: string;
  type: "locality" | "neighborhood" | "sublocality";
  distanceKm: number;
}

const US_STATE_TO_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH",
  "new jersey": "NJ", "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD", tennessee: "TN",
  texas: "TX", utah: "UT", vermont: "VT", virginia: "VA", washington: "WA",
  "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};

function toStateAbbr(state: string | null | undefined): string {
  if (!state) return "";
  const trimmed = state.trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return US_STATE_TO_ABBR[trimmed.toLowerCase()] || trimmed;
}

function NearbyAreaSuggestions({ locationId, state }: { locationId: string; state?: string | null }) {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ areas: NearbyArea[]; reason?: string }>({
    queryKey: ["/api/locations", locationId, "nearby-service-areas"],
    enabled: !!locationId,
    staleTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-6 w-20 rounded-full" />
        ))}
      </div>
    );
  }
  if (!data?.areas?.length) return null;

  const stateAbbr = toStateAbbr(state);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: text });
    } catch {
      toast({ title: "Copy failed", description: "Clipboard not available", variant: "destructive" });
    }
  };

  return (
    <div className="mt-2" data-testid="container-nearby-service-areas">
      <p className="text-[11px] text-muted-foreground mb-1.5">
        Aria suggests adding these {data.areas.length} nearby areas to your service area. Click to copy:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {data.areas.map((a, i) => {
          const formatted = stateAbbr ? `${a.name}, ${stateAbbr}` : a.name;
          return (
            <button
              key={`${a.name}-${i}`}
              type="button"
              onClick={() => handleCopy(formatted)}
              className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-[11px] font-normal text-foreground transition-colors hover:bg-muted hover:border-border cursor-pointer"
              data-testid={`badge-nearby-area-${i}`}
              title={`Click to copy "${formatted}" · ${a.type} · ${a.distanceKm.toFixed(1)} km away`}
            >
              {formatted}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ProfileField {
  key: string;
  label: string;
  category: "about" | "contact" | "location" | "hours" | "more";
  status: "complete" | "incomplete" | "partial";
  value?: string;
  tip?: string;
}

interface ProfileCompleteness {
  score: number;
  totalFields: number;
  completedFields: number;
  fields: ProfileField[];
  error?: string;
  stale?: boolean;
  snappedAt?: string | null;
}

export default function ProfileHealthPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;
  const location = locations?.find(l => l.id === locationId);

  const { data: profile, isLoading, refetch, isRefetching, dataUpdatedAt } = useQuery<ProfileCompleteness>({
    queryKey: ["/api/gbp/profile-completeness", locationId],
    enabled: !!locationId,
  });

  // Server-side, fetching profile completeness auto-populates location fields
  // (website / phone / address / category) from GBP into the workspace
  // location row. Invalidate the locations cache after each successful fetch
  // so the rest of the app (Road to Domination, Settings, etc.) sees the
  // freshly synced values without requiring a manual refresh.
  useEffect(() => {
    if (profile && !profile.error) {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
    }
  }, [dataUpdatedAt, profile]);

  const { data: scopeCheck, refetch: refetchScope, isRefetching: isScopeRefetching } = useQuery<{ hasGbpScope: boolean }>({
    queryKey: ["/api/gbp/scope-check"],
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const handleRecheck = async () => {
    await Promise.all([
      refetchScope(),
      refetch(),
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] }),
    ]);
  };
  const isAnyRefetching = isRefetching || isScopeRefetching;

  const handleConnectGbp = async () => {
    try {
      const res = await fetch("/api/google/auth-url?returnTo=/profile-health");
      const data = await res.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch {
      toast({ title: "Error", description: "Could not start Google connection flow", variant: "destructive" });
    }
  };

  const verifyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/locations/${locationId}`, {
        profileVerifiedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Profile verified", description: "Your profile has been marked as manually verified. You'll be reminded to re-verify in 30 days." });
    },
  });

  const verifiedAt = location?.profileVerifiedAt ? new Date(location.profileVerifiedAt) : null;
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  const isVerificationExpired = !verifiedAt || (Date.now() - verifiedAt.getTime() > thirtyDaysMs);
  const daysUntilExpiry = verifiedAt ? Math.max(0, Math.ceil((verifiedAt.getTime() + thirtyDaysMs - Date.now()) / (24 * 60 * 60 * 1000))) : 0;

  const categoryLabels: Record<string, string> = {
    about: "About Your Business",
    contact: "Contact Information",
    location: "Location & Service Area",
    hours: "Hours",
    more: "More Details",
  };

  const categoryOrder: string[] = ["about", "contact", "location", "hours", "more"];

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-profile-health-title">Profile Essentials</h1>
          <p className="text-sm text-muted-foreground mt-1">Audit your Google Business Profile completeness</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RtdOptOut
            locationId={locationId}
            taskIds={["essentials"]}
            stageName="Profile Essentials"
            warningTitle="Skip Profile Essentials for now?"
            warningBody={
              <p>
                Filling out hours, service areas, attributes, and contact details signals to Google
                that your profile is well-maintained — which directly impacts ranking and customer
                trust. Skipping this usually slows down ranking growth.
              </p>
            }
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecheck}
            disabled={isAnyRefetching}
            data-testid="button-refresh-profile"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isAnyRefetching ? "animate-spin" : ""}`} />
            {isAnyRefetching ? "Checking..." : "Re-check"}
          </Button>
        </div>
      </div>
      <RtdOptOutBanner
        locationId={locationId}
        taskIds={["essentials"]}
        stageName="Profile Essentials"
      />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : !profile || profile.error ? (
        <Card className="border-border/50">
          <CardContent className="py-8 text-center">
            {scopeCheck && !scopeCheck.hasGbpScope ? (
              <>
                <Link2 className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                <p className="text-sm font-medium" data-testid="text-gbp-scope-needed">Google Business Profile not connected</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Connect your Google Business Profile to enable profile health monitoring, post scheduling, and more.
                </p>
                <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConnectGbp} data-testid="button-connect-gbp">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect Google Business Profile
                </Button>
              </>
            ) : (
              <>
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Unable to fetch profile data</p>
                <p className="text-xs text-muted-foreground mt-1">{profile?.error || "Connect your Google Business Profile to enable profile health monitoring."}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()} data-testid="button-retry-profile">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {(() => {
            const scoreColor = profile.score >= 80 ? "text-emerald-400" : profile.score >= 50 ? "text-amber-400" : "text-red-400";
            const ringColor = profile.score >= 80 ? "stroke-emerald-500" : profile.score >= 50 ? "stroke-amber-500" : "stroke-red-500";
            const circumference = 2 * Math.PI * 45;
            const dashOffset = circumference - (profile.score / 100) * circumference;
            const incompleteCount = profile.fields.filter(f => f.status === "incomplete").length;
            const partialCount = profile.fields.filter(f => f.status === "partial").length;

            const grouped: Record<string, ProfileField[]> = {};
            for (const field of profile.fields) {
              if (!grouped[field.category]) grouped[field.category] = [];
              grouped[field.category].push(field);
            }

            const cardBorderColor = isVerificationExpired ? "border-amber-500/50" : "border-emerald-500/30";
            const cardRingColor = isVerificationExpired ? "stroke-amber-500" : ringColor;
            const cardScoreColor = isVerificationExpired ? "text-amber-400" : scoreColor;

            const gbpManagerUrl = (() => {
              // Open the Google Business Profile Manager directly. A specific
              // /dashboard/l/{id} deep link can loop back to login when the user
              // has multiple Google accounts, so we land on the GBP dashboard
              // where the signed-in owner selects their listing. (Avoid the
              // search-based URL — it just dumps users on a Google results page.)
              return "https://business.google.com/dashboard";
            })();

            const showStaleBanner = profile.stale || (scopeCheck && !scopeCheck.hasGbpScope);
            const snappedAtDate = profile.snappedAt ? new Date(profile.snappedAt) : null;

            return (
              <div className="space-y-5">
                {showStaleBanner && (
                  <Card className="border-amber-500/50 bg-amber-500/5" data-testid="banner-stale-snapshot">
                    <CardContent className="p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-amber-300">
                          {scopeCheck && !scopeCheck.hasGbpScope
                            ? "Google connection expired — showing your last saved profile"
                            : "Showing your last saved profile"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {snappedAtDate
                            ? `Last fetched ${snappedAtDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}.`
                            : ""}{" "}
                          {scopeCheck && !scopeCheck.hasGbpScope
                            ? "Reconnect Google Business Profile to refresh."
                            : "Click Re-check to pull fresh data."}
                        </p>
                        {scopeCheck && !scopeCheck.hasGbpScope && (
                          <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConnectGbp} data-testid="button-reconnect-gbp">
                            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Reconnect Google Business Profile
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                <Card className={`${cardBorderColor}`} data-testid="card-manual-verification">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <div className="relative w-28 h-28 shrink-0">
                        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
                          <circle cx="50" cy="50" r="45" fill="none" strokeWidth="6" strokeLinecap="round"
                            className={cardRingColor}
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            style={{ transition: "stroke-dashoffset 0.6s ease" }}
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className={`text-2xl font-bold ${cardScoreColor}`} data-testid="text-profile-score">{profile.score}%</span>
                          <span className="text-[10px] text-muted-foreground">Complete</span>
                        </div>
                      </div>

                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold">Profile Essentials Score</h3>
                          {!isVerificationExpired && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              Reviewed
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span>{profile.completedFields} completed</span>
                          </span>
                          {partialCount > 0 && (
                            <span className="flex items-center gap-1.5">
                              <CircleDot className="w-4 h-4 text-amber-500" />
                              <span>{partialCount} partial</span>
                            </span>
                          )}
                          {incompleteCount > 0 && (
                            <span className="flex items-center gap-1.5">
                              <AlertCircle className="w-4 h-4 text-red-400" />
                              <span>{incompleteCount} missing</span>
                            </span>
                          )}
                        </div>

                        {isVerificationExpired ? (
                          <div className="flex items-start gap-2 mt-1 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-amber-300 font-medium">Time for your monthly accuracy check</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">Your Google listing is verified — this is just a 30-day reminder to confirm your hours, services, and contact info are still up to date.</p>
                              <div className="flex items-center gap-2 mt-2">
                                <a href={gbpManagerUrl} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20 transition-colors text-[11px] font-medium"
                                  data-testid="link-gbp-management"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Open GBP Manager
                                </a>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-[11px] px-2.5"
                                  onClick={() => verifyMutation.mutate()}
                                  disabled={verifyMutation.isPending}
                                  data-testid="button-mark-verified"
                                >
                                  <ShieldCheck className="w-3 h-3 mr-1" />
                                  {verifyMutation.isPending ? "Saving..." : "Mark as Verified"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3.5 h-3.5" />
                            <span>Verified {verifiedAt?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · Next verification in {daysUntilExpiry} days</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {categoryOrder.map(cat => {
                  const fields = grouped[cat];
                  if (!fields || fields.length === 0) return null;

                  return (
                    <div key={cat}>
                      <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">{categoryLabels[cat]}</h3>
                      <div className="space-y-1.5">
                        {fields.map(field => (
                          <Card key={field.key} className="border-border/50" data-testid={`card-profile-${field.key}`}>
                            <CardContent className="p-3.5">
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 shrink-0">
                                  {field.status === "complete" ? (
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                  ) : field.status === "partial" ? (
                                    <CircleDot className="w-5 h-5 text-amber-500" />
                                  ) : (
                                    <AlertCircle className="w-5 h-5 text-red-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <h4 className="text-sm font-semibold">{field.label}</h4>
                                    <Badge className={`text-[10px] shrink-0 ${
                                      field.status === "complete" ? "bg-emerald-500/20 text-emerald-400" :
                                      field.status === "partial" ? "bg-amber-500/20 text-amber-400" :
                                      "bg-red-500/20 text-red-400"
                                    }`}>
                                      {field.status === "complete" ? "Complete" : field.status === "partial" ? "Needs more" : "Missing"}
                                    </Badge>
                                  </div>
                                  {field.value && (
                                    <p className="text-xs text-foreground/80 mt-0.5 truncate">{field.value}</p>
                                  )}
                                  {field.status !== "complete" && field.tip && (
                                    <p className="text-xs text-muted-foreground mt-1">{field.tip}</p>
                                  )}
                                  {field.status !== "complete" && field.key === "serviceArea" && locationId && (
                                    <NearbyAreaSuggestions locationId={locationId} state={location?.state} />
                                  )}
                                  {field.status !== "complete" && field.status !== "partial" && (
                                    <a
                                      href={gbpManagerUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-md bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20 transition-colors text-[11px] font-medium"
                                      data-testid={`link-gbp-fix-${field.key}`}
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      Fix in GBP Manager
                                    </a>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
